import axios from 'axios';
import { designerItemsAPI } from './designerItemsAPI';
import { boutiqueItemsAPI } from './boutiqueItemsAPI';
import {
  expandWeddingCategoryQueryValues,
  getWeddingCategoryValuesForDepartment,
  getWeddingFootwearCrossListQueries,
} from '../constants/weddingCategories';
import { getMarketplaceApiBaseUrl } from './resolveApiBaseUrl';
import { sortBrowseItems } from '../utils/sortBrowseItems';

const API_BASE_URL = process.env.REACT_APP_ITEMS_API_URL ||
  getMarketplaceApiBaseUrl() || 'http://localhost:8080/api';

const emptyPage = (page, pageSize) => ({
  results: [],
  count: 0,
  page,
  page_size: pageSize,
  total_pages: 0,
  has_next: false,
});

function normalizeMarketplaceItem(item) {
  const images = item.images || [];
  const primary = item.primary_image || item.image_url || (images[0]?.image ?? images[0]);
  return {
    key: `item-${item.id}`,
    listingKind: 'item',
    id: item.id,
    title: item.title,
    price: item.price,
    category: item.category,
    condition: item.condition,
    brand_name: item.brand_name ?? null,
    custom_brand_name: item.custom_brand_name ?? null,
    is_no_brand: item.is_no_brand ?? null,
    item_code: item.item_code,
    images,
    image_url: primary,
    brand_logo: null,
    created_at: item.created_at,
    distance_km: item.distance_km ?? null,
    is_luxury: item.is_luxury ?? null,
    listing_type: item.listing_type ?? null,
    is_hijabi: item.is_hijabi ?? null,
  };
}

function normalizeDesignerOrBoutiqueItem(item, listingKind) {
  const title = item.title || `${item.brand_name || ''} ${item.item_name || ''}`.trim() || 'Listing';
  const images = item.images || [];
  const primary = item.primary_image || (images[0]?.image ?? images[0]);
  return {
    key: `${listingKind}-${item.id}`,
    listingKind,
    id: item.id,
    title,
    price: item.price,
    category: item.category,
    condition: item.condition,
    brand_name: item.brand_name ?? null,
    custom_brand_name: item.custom_brand_name ?? null,
    is_no_brand: item.is_no_brand ?? null,
    item_code: item.item_code,
    images,
    image_url: primary,
    brand_logo: item.brand_logo,
    created_at: item.created_at,
    distance_km: item.distance_km ?? null,
  };
}

function dedupeListings(rows) {
  const seen = new Set();
  return (rows || []).filter((row) => {
    const key = row?.key || `${row?.listingKind || 'item'}-${row?.id}`;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function withCategoryList(filters, categories) {
  const next = { ...filters };
  delete next.category;
  delete next.categories;
  const values = [...new Set((categories || []).map((value) => String(value || '').trim()).filter(Boolean))];
  if (values.length === 1) {
    next.category = values[0];
  } else if (values.length > 1) {
    // `categories=` is the real filter. Also send `category=` so origins that
    // only keep wedding rows for a single wedding category still return them.
    next.category = values[0];
    next.categories = values.join(',');
  }
  return next;
}

async function getMarketplaceItemsPage(filters, page, pageSize) {
  const params = new URLSearchParams();
  Object.keys(filters).forEach((key) => {
    const value = filters[key];
    if (value === undefined || value === null || value === '') return;
    params.append(key, String(value));
  });
  params.append('page', String(page));
  params.append('page_size', String(pageSize));
  const response = await axios.get(`${API_BASE_URL}/public/items/?${params.toString()}`);
  const data = response.data;
  if (Array.isArray(data)) {
    return {
      results: data.map(normalizeMarketplaceItem),
      count: data.length,
      page: 1,
      page_size: data.length,
      has_next: false,
    };
  }
  const results = (data.results || []).map(normalizeMarketplaceItem);
  return { ...data, results };
}

async function fetchFootwearCrossList(marketplaceFilters, page, pageSize, department, category) {
  const queries = getWeddingFootwearCrossListQueries({
    department,
    category,
    subcategory: marketplaceFilters.subcategory || '',
  });
  if (!queries.length) return { results: [], has_next: false };

  // Drop wedding subcategory before Footwear queries — paths are Women > Heels, etc.
  const base = { ...marketplaceFilters };
  delete base.category;
  delete base.subcategory;

  const footwearCategory = queries[0].category;
  const subcategories = [...new Set(queries.map((query) => query.subcategory).filter(Boolean))];
  const shared = subcategories.length
    ? { ...base, category: footwearCategory, subcategories: subcategories.join(',') }
    : { ...base, category: footwearCategory };

  const pages = await Promise.all([
    getMarketplaceItemsPage(shared, page, pageSize).catch(() => emptyPage(page, pageSize)),
    // Luxury footwear is excluded from the default public items query.
    getMarketplaceItemsPage(
      { ...shared, is_luxury: 'true' },
      page,
      pageSize,
    ).catch(() => emptyPage(page, pageSize)),
  ]);

  return {
    results: dedupeListings(pages.flatMap((pageResult) => pageResult.results || [])),
    has_next: pages.some((pageResult) => pageResult.has_next),
  };
}

async function fetchSourcePage(fetcher, filters, page, perSource) {
  try {
    const payload = await fetcher(filters, page, perSource);
    const rows = Array.isArray(payload?.results) ? payload.results : [];
    return {
      rows: rows.map((row) =>
        row.listingKind
          ? row
          : normalizeDesignerOrBoutiqueItem(
              row,
              fetcher === designerItemsAPI.getDesignerItemsPage ? 'designer_item' : 'boutique_item',
            ),
      ),
      has_next: Boolean(payload?.has_next),
    };
  } catch {
    return { rows: [], has_next: false };
  }
}

export const weddingEssentialsAPI = {
  getWeddingListingsPage: async (filters = {}, page = 1, pageSize = 24, sort = '') => {
    const category = filters.category || '';
    const apiFilters = { ...filters };
    delete apiFilters.view;
    if (apiFilters.subcategory && apiFilters.category === 'Books') {
      apiFilters.book_subcategory = apiFilters.subcategory;
    }
    const effectiveSort = sort === 'nearest' ? 'recommended' : sort;
    if (effectiveSort && effectiveSort !== 'recommended') {
      apiFilters.sort = effectiveSort;
    }
    // Department is already applied by which wedding categories we query.
    // Marketplace department filters only understand Clothing taxonomies and would
    // drop wedding-category Item rows (e.g. bridal / bridesmaids).
    const marketplaceFilters = { ...apiFilters };
    delete marketplaceFilters.department;

    const weddingCategories = getWeddingCategoryValuesForDepartment(apiFilters.department || '');
    const categoryValues = category
      ? expandWeddingCategoryQueryValues(category)
      : weddingCategories;
    if (!category) {
      const sortKey = effectiveSort && effectiveSort !== 'recommended' ? effectiveSort : 'newest';
      marketplaceFilters.sort = sortKey;
      apiFilters.sort = sortKey;
    }

    const fetchSize = pageSize * Math.max(page, 1);
    const marketFilters = withCategoryList(marketplaceFilters, categoryValues);
    const catalogFilters = withCategoryList(apiFilters, categoryValues);
    delete catalogFilters.is_hijabi;
    const [market, designer, boutique, footwearCross] = await Promise.all([
      getMarketplaceItemsPage(marketFilters, 1, fetchSize).catch(() => emptyPage(1, fetchSize)),
      fetchSourcePage(
        designerItemsAPI.getDesignerItemsPage.bind(designerItemsAPI),
        catalogFilters,
        1,
        fetchSize,
      ),
      fetchSourcePage(
        boutiqueItemsAPI.getBoutiqueItemsPage.bind(boutiqueItemsAPI),
        catalogFilters,
        1,
        fetchSize,
      ),
      fetchFootwearCrossList(
        marketplaceFilters,
        1,
        fetchSize,
        apiFilters.department || '',
        category,
      ),
    ]);

    const mergedRows = dedupeListings([
        ...(market.results || []),
        ...designer.rows,
        ...boutique.rows,
        ...(footwearCross.results || []),
      ]);
    const hijabiFilter = apiFilters.is_hijabi;
    const filteredRows = hijabiFilter === 'true' || hijabiFilter === 'false'
      ? mergedRows.filter((row) => row.is_hijabi === (hijabiFilter === 'true'))
      : mergedRows;
    const merged = sortBrowseItems(
      filteredRows,
      sort || (category ? 'recommended' : 'newest'),
    );
    const start = (page - 1) * pageSize;
    const results = merged.slice(start, start + pageSize);

    return {
      results,
      count: merged.length,
      page,
      page_size: pageSize,
      total_pages: Math.ceil(merged.length / pageSize) || 1,
      has_next: Boolean(
        start + pageSize < merged.length
        || market.has_next
        || designer.has_next
        || boutique.has_next
        || footwearCross.has_next
      ),
    };
  },
};
